import React from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { toggleChat } from '../../store/chatBotSlice';
import { BotMessageSquare, ChevronRight } from 'lucide-react';

interface ChatTriggerProps {
  isOpen: boolean;
  unreadCount: number;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  isDragging?: boolean;
}

export const ChatTrigger: React.FC<ChatTriggerProps> = ({
  isOpen,
  unreadCount,
  isConnected,
  connectionStatus,
  isDragging = false
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [animationDuration, setAnimationDuration] = React.useState(8);

  // Set random animation duration on mount and after each cycle
  React.useEffect(() => {
    const setRandomDuration = () => {
      const durations = [8, 10, 15];
      const randomDuration = durations[Math.floor(Math.random() * durations.length)];
      setAnimationDuration(randomDuration);
    };

    setRandomDuration();

    // Update duration after each animation cycle
    const interval = setInterval(() => {
      setRandomDuration();
    }, animationDuration * 1000);

    return () => clearInterval(interval);
  }, [animationDuration]);

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click if we were dragging (user moved mouse significantly)
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    dispatch(toggleChat());
  };

  const getConnectionIndicator = () => {
    switch (connectionStatus) {
      case 'connecting':
        return (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse">
            <div className="absolute inset-0 w-full h-full bg-yellow-400 rounded-full animate-ping"></div>
          </div>
        );
      case 'connected':
        return (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full">
            <div className="absolute inset-1 w-1 h-1 bg-green-600 rounded-full"></div>
          </div>
        );
      case 'error':
        return (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full">
            <div className="absolute inset-1 w-1 h-1 bg-red-600 rounded-full"></div>
          </div>
        );
      default:
        return null;
    }
  };

  const getSupportIcon = () => {
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          width: isOpen ? '48px' : '60px',
          height: isOpen ? '48px' : '60px',
          borderRadius: '50%',
          border: '1px solid #D9F0FF',
          transition: 'all 0.3s ease',
          animation: isOpen ? 'none' : `border-swirl ${animationDuration}s linear infinite`
        }}
      >
        <div style={{
          animation: isOpen ? 'none' : `rotate-icon ${animationDuration}s ease-in-out infinite`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transformStyle: 'preserve-3d',
          perspective: '1000px'
        }}>
          {isOpen ? (
            <ChevronRight size={isOpen ? 24 : 32} color="#0f3340" strokeWidth={2} />
          ) : (
            <BotMessageSquare size={32} color="#0f3340" strokeWidth={2} />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .chat-trigger:hover + .chat-trigger-hint,
        .chat-trigger-hint:hover {
          opacity: 1 !important;
        }

        @keyframes swirl-shadow {
          0% {
            box-shadow:
              8px 0px 20px rgba(217, 240, 255, 0.6),
              0px 8px 20px rgba(217, 240, 255, 0.4),
              -8px 0px 20px rgba(217, 240, 255, 0.2),
              0px -8px 20px rgba(217, 240, 255, 0.2);
          }
          8.33% {
            box-shadow:
              0px 8px 20px rgba(217, 240, 255, 0.6),
              -8px 0px 20px rgba(217, 240, 255, 0.4),
              0px -8px 20px rgba(217, 240, 255, 0.2),
              8px 0px 20px rgba(217, 240, 255, 0.2);
          }
          16.67% {
            box-shadow:
              -8px 0px 20px rgba(217, 240, 255, 0.6),
              0px -8px 20px rgba(217, 240, 255, 0.4),
              8px 0px 20px rgba(217, 240, 255, 0.2),
              0px 8px 20px rgba(217, 240, 255, 0.2);
          }
          25% {
            box-shadow:
              0px -8px 20px rgba(217, 240, 255, 0.6),
              8px 0px 20px rgba(217, 240, 255, 0.4),
              0px 8px 20px rgba(217, 240, 255, 0.2),
              -8px 0px 20px rgba(217, 240, 255, 0.2);
          }
          33.33% {
            box-shadow:
              8px 0px 20px rgba(217, 240, 255, 0.6),
              0px 8px 20px rgba(217, 240, 255, 0.4),
              -8px 0px 20px rgba(217, 240, 255, 0.2),
              0px -8px 20px rgba(217, 240, 255, 0.2);
          }
          33.34%, 100% {
            box-shadow:
              8px 0px 20px rgba(217, 240, 255, 0.6),
              0px 8px 20px rgba(217, 240, 255, 0.4),
              -8px 0px 20px rgba(217, 240, 255, 0.2),
              0px -8px 20px rgba(217, 240, 255, 0.2);
          }
        }

        @keyframes rotate-icon {
          0% {
            transform: rotateY(0deg);
          }
          12.5% {
            transform: rotateY(360deg);
          }
          12.51%, 100% {
            transform: rotateY(360deg);
          }
        }

        @keyframes border-swirl {
          0% {
            border-color: #D9F0FF;
            box-shadow:
              3px 0 0 0 #99ebff,
              0 3px 0 0 #D9F0FF,
              -3px 0 0 0 #D9F0FF,
              0 -3px 0 0 #D9F0FF;
          }
          3.125% {
            border-color: #D9F0FF;
            box-shadow:
              3px 0 0 0 #D9F0FF,
              0 3px 0 0 #99ebff,
              -3px 0 0 0 #D9F0FF,
              0 -3px 0 0 #D9F0FF;
          }
          6.25% {
            border-color: #D9F0FF;
            box-shadow:
              3px 0 0 0 #D9F0FF,
              0 3px 0 0 #D9F0FF,
              -3px 0 0 0 #99ebff,
              0 -3px 0 0 #D9F0FF;
          }
          9.375% {
            border-color: #D9F0FF;
            box-shadow:
              3px 0 0 0 #D9F0FF,
              0 3px 0 0 #D9F0FF,
              -3px 0 0 0 #D9F0FF,
              0 -3px 0 0 #99ebff;
          }
          12.5% {
            border-color: #D9F0FF;
            box-shadow:
              3px 0 0 0 #99ebff,
              0 3px 0 0 #D9F0FF,
              -3px 0 0 0 #D9F0FF,
              0 -3px 0 0 #D9F0FF;
          }
          12.51%, 100% {
            border-color: #D9F0FF;
            box-shadow: none;
          }
        }

        @keyframes swirl-on-hover {
          0% {
            box-shadow:
              8px 0px 20px rgba(217, 240, 255, 0.6),
              0px 8px 20px rgba(217, 240, 255, 0.4),
              -8px 0px 20px rgba(217, 240, 255, 0.2),
              0px -8px 20px rgba(217, 240, 255, 0.2);
          }
          25% {
            box-shadow:
              0px 8px 20px rgba(217, 240, 255, 0.6),
              -8px 0px 20px rgba(217, 240, 255, 0.4),
              0px -8px 20px rgba(217, 240, 255, 0.2),
              8px 0px 20px rgba(217, 240, 255, 0.2);
          }
          50% {
            box-shadow:
              -8px 0px 20px rgba(217, 240, 255, 0.6),
              0px -8px 20px rgba(217, 240, 255, 0.4),
              8px 0px 20px rgba(217, 240, 255, 0.2),
              0px 8px 20px rgba(217, 240, 255, 0.2);
          }
          75% {
            box-shadow:
              0px -8px 20px rgba(217, 240, 255, 0.6),
              8px 0px 20px rgba(217, 240, 255, 0.4),
              0px 8px 20px rgba(217, 240, 255, 0.2),
              -8px 0px 20px rgba(217, 240, 255, 0.2);
          }
          100% {
            box-shadow:
              8px 0px 20px rgba(217, 240, 255, 0.6),
              0px 8px 20px rgba(217, 240, 255, 0.4),
              -8px 0px 20px rgba(217, 240, 255, 0.2),
              0px -8px 20px rgba(217, 240, 255, 0.2);
          }
        }
      `}</style>
      <button
        onClick={handleClick}
        style={{
          width: isOpen ? '48px' : '60px',
          height: isOpen ? '48px' : '60px',
          borderRadius: '50%',
          border:'3px solid #D9F0FF',
          position: 'relative',
          cursor: 'pointer',
          background: 'transparent',
          animation: `swirl-shadow ${animationDuration}s ease-in-out infinite`,
          transition: 'all 0.3s ease',
          opacity: !isConnected && connectionStatus !== 'connecting' ? 0.85 : 1,
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.animation = 'swirl-on-hover 1s ease-in-out infinite';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.animation = `swirl-shadow ${animationDuration}s ease-in-out infinite`;
        }}
        className="chat-trigger group"
        aria-label={isOpen ? "Close chat" : "Open support chat"}
        aria-expanded={isOpen}
        role="button"
        title={
          !isConnected && connectionStatus !== 'connecting'
            ? "Chat service - click to retry"
            : isOpen
              ? "Close chat"
              : ""
        }
      >
      {/* Main Icon */}
      <div className="flex items-center justify-center w-full h-full">
        {getSupportIcon()}
      </div>

      {/* Unread Count Badge */}
      {!isOpen && unreadCount > 0 && (
        <div className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
          {unreadCount > 99 ? '99+' : unreadCount}
        </div>
      )}

      {/* Connection Status Indicator */}
      {getConnectionIndicator()}

      {/* Pulse Effect for New Messages */}
      {!isOpen && unreadCount > 0 && (
        <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20"></div>
      )}


      {/* Loading Spinner for Connecting State */}
      {connectionStatus === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white border-opacity-30 border-t-white rounded-full animate-spin"></div>
        </div>
      )}
    </button>

    {/* Hint label above button - only when chat is closed, shows on hover */}
    {!isOpen && (
      <div
        className="chat-trigger-hint"
        style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <div style={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#6b7280',
          marginBottom: '2px'
        }}>
          Click for support chat
        </div>
        <div style={{
          fontSize: '9px',
          color: '#6b7280'
        }}>
          Drag to move
        </div>
      </div>
    )}
    </>
  );
};

export default ChatTrigger;